import { FloatingFormItem, PlanAccountsSelect } from '@/components/_common';
import DocumentTypesSelectField from '@/components/_common/DocumentTypesSelectField';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import {
  CashMovementReportFormInterface,
  CashMovementReportRequestInterface,
  DateTypeEnum,
  ReportTypeEnum,
  TypeOfMovementEnum,
} from '@/core/domain/financial/BoxMovement/BoxMovement.types';
import { QuestResponseType } from '@/core/domain/questReport/Reports/AccountsPayable/AccountsPayable.types';
import { useBoxMovementReport } from '@/core/domain/questReport/Reports/BoxMovement/BoxMovement.hook';
import {
  QuestBoxMovementRequest,
  REPORT_BOX_MOVEMENT_DATE_FILTER,
  REPORT_BOX_MOVEMENT_REPORT_TYPE,
  REPORT_BOX_MOVEMENT_TYPE,
} from '@/core/domain/questReport/Reports/BoxMovement/BoxMovement.types';
import useCommonGenerateReport from '@/core/domain/reports/commons/Report.hook';
import useFileLoading, { FileType } from '@/hooks/useFileLoading';
import useReportGenerate, { ReportCallback } from '@/hooks/useReportGenerate';
import { DateTimeFormat } from '@/utils/moment';
import { Button, Checkbox, Form, Modal, Radio, Tooltip } from 'antd';
import moment, { Moment } from 'moment';

interface CashMovementReportModalInterface {
  open: boolean;
  onCancel: () => void;
  bankAccountIds?: number[];
  companiesIds?: number[];
  initialDate?: Moment;
  finalDate?: Moment;
}

export default function CashMovementReportModal({
  open,
  onCancel,
  bankAccountIds,
  companiesIds,
  initialDate,
  finalDate,
}: CashMovementReportModalInterface) {
  const [form] = Form.useForm<CashMovementReportFormInterface>();
  const { fileLoading, setFileLoading, setFileUnloading, FILE_FORMAT } =
    useFileLoading();
  const report = useReportGenerate();

  const hidePreviousBalance = Form.useWatch('hidePreviousBalance', form);

  const dateTypeOptions = [
    { label: 'Lançamento', value: DateTypeEnum.Launch },
    { label: 'Conciliação', value: DateTypeEnum.Conciliation },
  ];

  const reportTypeOptions = [
    { label: 'Geral', value: ReportTypeEnum.Geral },
    { label: 'Plano de contas', value: ReportTypeEnum.PlanAccount },
  ];

  const typeOfMovementOptions = [
    { label: 'Geral', value: TypeOfMovementEnum.General },
    { label: 'Tarifas', value: TypeOfMovementEnum.Tariffs },
  ];

  const generateReport = useCommonGenerateReport(
    '/generate/cash-movement',
    'Relatório gerado com sucesso!',
    'Relatório de movimento de caixa gerado com sucesso.',
  );

  const questGenerateReport = useBoxMovementReport();

  const handleGenerateReport = async (
    format: FileType,
    callback?: ReportCallback,
  ) => {
    setFileLoading(format);
    try {
      if (initialDate && finalDate) {
        const values = await form.validateFields();
        const obj: CashMovementReportRequestInterface = {
          ...values,
          format: FILE_FORMAT[format],
          bankAccountIds,
          companiesIds,
          initialDate: initialDate.format('yyyy-MM-DD'),
          finalDate: finalDate.format('yyyy-MM-DD'),
        };

        const questObj: QuestBoxMovementRequest = {
          companiesIds: companiesIds?.toString(),
          planAccountIds: values.planAccountIds?.toString(),
          bankAccountIds: bankAccountIds?.toString(),
          documentTypeIds: values.documentTypeIds?.toString(),
          startDate: initialDate.format(DateTimeFormat),
          endDate: finalDate.format(DateTimeFormat),
          responseType: QuestResponseType.CSV,
          dateFilter: REPORT_BOX_MOVEMENT_DATE_FILTER[values.dateType],
          movementType: REPORT_BOX_MOVEMENT_TYPE[values.typeOfMovement],
          reportType: REPORT_BOX_MOVEMENT_REPORT_TYPE[values.reportType],
        };

        const result =
          format === 'CSV'
            ? await questGenerateReport.mutateAsync(questObj)
            : await generateReport.mutateAsync(obj);

        if (callback) {
          callback(result);
        }
      }
    } catch {}
    setFileUnloading(format);
  };

  const isWithinRange = () => {
    if (!initialDate || !finalDate) {
      return false;
    }

    const startDate = moment(initialDate);
    const endDate = moment(startDate).add(6, 'months');
    const currentDate = moment(finalDate);

    return currentDate.isBetween(startDate, endDate);
  };

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title="Impressão de relatório"
      footer={
        <SpaceForButton justifyContent="flex-end">
          <Button block onClick={onCancel}>
            Cancelar
          </Button>

          <Button
            block
            type="primary"
            key="viewReport"
            loading={fileLoading.REPORT_VIEW}
            onClick={() =>
              handleGenerateReport('REPORT_VIEW', (result) =>
                report.openFile({
                  result,
                  fileName: 'movimento-de-caixa.pdf',
                  fileType: 'application/pdf',
                }),
              )
            }
          >
            Visualizar
          </Button>

          <Button
            block
            type="primary"
            key="PDF"
            loading={fileLoading.PDF}
            onClick={() =>
              handleGenerateReport('PDF', (result) =>
                report.downloadFile({
                  result,
                  fileName: 'movimento-de-caixa.pdf',
                }),
              )
            }
          >
            PDF
          </Button>

          <Tooltip
            title={
              !isWithinRange()
                ? 'O intervalo entre as datas do filtro é de até 6 meses.'
                : ''
            }
          >
            <Button
              block
              type="primary"
              key="CSV"
              loading={fileLoading.CSV}
              onClick={() =>
                handleGenerateReport('CSV', (result) =>
                  report.downloadFile({
                    result,
                    fileName: 'movimento-de-caixa.csv',
                  }),
                )
              }
              disabled={!isWithinRange() || hidePreviousBalance}
            >
              CSV
            </Button>
          </Tooltip>
        </SpaceForButton>
      }
      width={600}
    >
      <Form form={form} layout={'vertical'}>
        <Form.Item
          name="dateType"
          label="Selecione uma data"
          initialValue={DateTypeEnum.Launch}
        >
          <Radio.Group
            options={dateTypeOptions}
            buttonStyle="solid"
            optionType="button"
          />
        </Form.Item>

        <Form.Item
          name="reportType"
          label="Selecione o tipo de relatório"
          initialValue={ReportTypeEnum.Geral}
        >
          <Radio.Group
            options={reportTypeOptions}
            buttonStyle="solid"
            optionType="button"
          />
        </Form.Item>

        <Form.Item
          name="typeOfMovement"
          label="Tipo de Movimentação"
          initialValue={TypeOfMovementEnum.General}
        >
          <Radio.Group
            options={typeOfMovementOptions}
            buttonStyle="solid"
            optionType="button"
          />
        </Form.Item>

        <Form.Item name="hidePreviousBalance" valuePropName="checked">
          <Checkbox>Não exibir saldo anterior</Checkbox>
        </Form.Item>

        <FloatingFormItem name="documentTypeIds" label="Tipo de documento">
          <DocumentTypesSelectField mode="multiple" />
        </FloatingFormItem>

        <FloatingFormItem name="planAccountIds" label="Plano de contas">
          <PlanAccountsSelect mode="multiple" />
        </FloatingFormItem>
      </Form>
    </Modal>
  );
}
